import type { PortalLocale } from '../../lib/locale';

/** Copy visible above Business-Day Close on the Register & cash page. */
export interface RegisterPageCopy {
  title: string;
  description: string;
  waitingTitle: string;
  waitingDescription: string;
  waiting: {
    nothing: string;
    countType: Record<'opening' | 'closing' | 'adhoc' | 'safe' | 'cajaita', string>;
    cash: string;
    openDrawer: string;
    checked: string;
    pouchInto: (name: string | null) => string;
    countedIntoBag: string;
    openAndCount: string;
    audit: {
      common: {
        youCounted: string;
        difference: string;
        done: string;
        note: string;
        onSheet: string;
      };
      sheet: {
        dollars: string;
        lempiras: string;
        blankReads: string;
        noteQuantity: (amount: string) => string;
        loadFailed: string;
        noneConfigured: string;
      };
      signOff: {
        notice: (allThere: boolean) => { title: string; message: string };
        problem: { title: string; message: string };
      };
      pouch: {
        title: string;
        notice: (allThere: boolean) => { title: string; message: string };
        problem: { title: string; message: string };
        wentIntoBag: string;
        exactResult: string;
        differentResult: string;
        noteDescription: string;
        record: string;
      };
      box: {
        title: (name: string | null) => string;
        notice: { title: string; message: string };
        problem: { title: string; message: string };
        ledgerExpected: string;
        resultDescription: string;
        contentsTitle: string;
        noteDescription: string;
        record: string;
      };
      standing: {
        title: string;
        description: string;
        cashCounted: string;
        receipts: string;
        together: string;
        shouldHold: string;
        short: string;
        over: string;
        joiner: string;
        exact: string;
        shortage: (differences: string) => string;
        overage: (differences: string) => string;
        mixed: (differences: string) => string;
      };
    };
  };
}

const EN: RegisterPageCopy = {
  title: 'Register & cash',
  description:
    'Drawers as they were counted, what the safe and the float box are holding, and what has gone to the bank. After midnight, activity remains on the business day when it started until the 4:00 AM cutoff.',
  waitingTitle: 'Waiting for you',
  waitingDescription:
    'Counts and drops nobody has checked yet. Nothing here is holding anything up — the bar goes on trading and the list is here when you are. Which counts appear is yours to set.',
  waiting: {
    nothing: 'Nothing is waiting for you. Counts appear here as they are taken.',
    countType: {
      opening: 'Float going in',
      closing: 'Counted off',
      adhoc: 'Mid-shift check',
      safe: 'Safe count',
      cajaita: 'Float box count',
    },
    cash: 'Cash',
    openDrawer: 'Open the drawer',
    checked: 'I have checked this',
    pouchInto: (name) => `Pouch into ${name ?? 'the safe'}`,
    countedIntoBag: 'counted into the bag at the end of the shift',
    openAndCount: 'Open it and count',
    audit: {
      common: {
        youCounted: 'You counted',
        difference: 'Difference',
        done: 'Done',
        note: 'Note',
        onSheet: 'On the sheet',
      },
      sheet: {
        dollars: 'Dollars',
        lempiras: 'Lempiras',
        blankReads: 'not counted',
        noteQuantity: (amount) => `How many ${amount} notes`,
        loadFailed:
          'The list of notes this bar takes could not be loaded, so there is nothing to type into yet. That is a connection problem, not an empty list — nothing here needs setting up. Close this and try again in a moment.',
        noneConfigured:
          'No notes have been listed for this bar yet, so there is nothing to count into. Add the bills you actually see in the box on the pricing screen first.',
      },
      signOff: {
        notice: (allThere) => ({
          title: 'Checked',
          message: allThere
            ? 'The figures agree, and your name is on the record beside them.'
            : 'Signed off with the difference on the record. It is not written off — it is written down.',
        }),
        problem: {
          title: 'Count Check Failed',
          message: 'Nothing changed. Check the connection and try again.',
        },
      },
      pouch: {
        title: 'Pouch Count',
        notice: (allThere) => ({
          title: allThere ? 'The Money Is All There' : 'The Difference Is on the Record',
          message: allThere
            ? 'What came out of the pouch is what went into it.'
            : 'What was in the pouch is recorded against what should have been. Count the safe to bring its figure back in line.',
        }),
        problem: {
          title: 'Pouch Count Failed',
          message: 'Nothing changed. Check the connection and try again.',
        },
        wentIntoBag: 'Went into the bag',
        exactResult: 'All there. Recorded with your name and the time.',
        differentResult:
          'Recorded, not written off. The safe still holds what the drop said it holds until you count the safe itself — that count is what brings its figure back in line.',
        noteDescription: 'Anything worth remembering. Whatever you write here is kept with the count.',
        record: 'Record what was in it',
      },
      box: {
        title: (name) => `${name ?? 'Cash Box'} Count`,
        notice: {
          title: 'Count Recorded',
          message:
            'This is what the box holds from now on. Everything that moves after it is added to this figure.',
        },
        problem: {
          title: 'Cash Box Count Failed',
          message: 'Nothing changed. Check the connection and try again.',
        },
        ledgerExpected: 'The ledger expected',
        resultDescription:
          'The box now reads what you counted, and the difference stays on the record rather than being absorbed into it. If it was carrying cash from before this system existed, that is what the difference is — and money can be banked out of it again.',
        contentsTitle: 'What is actually in it',
        noteDescription: 'Anything worth remembering about this count.',
        record: 'Record this count',
      },
      standing: {
        title: 'Receipts plus cash',
        description:
          'This box is kept stocked to a standing amount, and bills are paid straight out of it — so what you counted, plus the receipts on file since your last check, should still come to that amount. This is separate from the difference above, which measures the count against the ledger.',
        cashCounted: 'Cash you counted',
        receipts: 'Receipts since your last check',
        together: 'Together',
        shouldHold: 'It should hold',
        short: 'short',
        over: 'over',
        joiner: ' and ',
        exact:
          'Receipts plus cash still total what the box should hold. Every note not in it is covered by a bill on file.',
        shortage: (differences) =>
          `Receipts plus cash come up ${differences} against what the box should hold. A shortfall here is not covered by any recorded bill — either a payment was never recorded, or the money is gone.`,
        overage: (differences) =>
          `Receipts plus cash come to ${differences} more than the box should hold — it is carrying more than it is stocked to.`,
        mixed: (differences) =>
          `The per-currency comparison is ${differences}. A shortfall is not covered by a recorded bill; an overage means the box is carrying more than its standing amount.`,
      },
    },
  },
};

const ES: RegisterPageCopy = {
  title: 'Caja y efectivo',
  description:
    'Las cajas según sus conteos, lo que guardan la caja fuerte y la cajaita, y lo que se ha llevado al banco. Después de medianoche, la actividad permanece en el día de operación en que comenzó hasta el corte de las 4:00 a. m.',
  waitingTitle: 'Pendiente para vos',
  waitingDescription:
    'Conteos y entregas que nadie ha revisado todavía. Nada de esto detiene el trabajo: el bar sigue operando y la lista queda aquí para cuando podás revisarla. Vos decidís qué conteos aparecen.',
  waiting: {
    nothing: 'No hay nada pendiente para vos. Los conteos aparecen aquí cuando se realizan.',
    countType: {
      opening: 'Fondo inicial',
      closing: 'Conteo de cierre',
      adhoc: 'Conteo durante el turno',
      safe: 'Conteo de caja fuerte',
      cajaita: 'Conteo de cajaita',
    },
    cash: 'Efectivo',
    openDrawer: 'Abrir la caja',
    checked: 'Ya lo revisé',
    pouchInto: (name) => `Bolsa depositada en ${name ?? 'la caja fuerte'}`,
    countedIntoBag: 'contado en la bolsa al final del turno',
    openAndCount: 'Abrir y contar',
    audit: {
      common: {
        youCounted: 'Contaste',
        difference: 'Diferencia',
        done: 'Listo',
        note: 'Nota',
        onSheet: 'Total contado',
      },
      sheet: {
        dollars: 'Dólares',
        lempiras: 'Lempiras',
        blankReads: 'sin contar',
        noteQuantity: (amount) => `Cantidad de billetes de ${amount}`,
        loadFailed:
          'No se pudo cargar la lista de billetes que acepta el bar, así que todavía no hay nada que ingresar. Es un problema de conexión, no una lista vacía; no hace falta configurar nada aquí. Cerrá esta ventana e intentá de nuevo en un momento.',
        noneConfigured:
          'Todavía no hay billetes configurados para este bar, así que no hay nada que contar. Agregá primero en la pantalla de precios los billetes que realmente ves en la caja.',
      },
      signOff: {
        notice: (allThere) => ({
          title: 'Revisado',
          message: allThere
            ? 'Las cifras coinciden y tu nombre quedó en el registro.'
            : 'La revisión quedó aprobada con la diferencia registrada. No se eliminó: quedó documentada.',
        }),
        problem: {
          title: 'No se pudo revisar el conteo',
          message: 'No se cambió nada. Revisá la conexión e intentá de nuevo.',
        },
      },
      pouch: {
        title: 'Conteo de bolsa',
        notice: (allThere) => ({
          title: allThere ? 'Está todo el dinero' : 'La diferencia quedó registrada',
          message: allThere
            ? 'Lo que salió de la bolsa coincide con lo que entró.'
            : 'Lo que había en la bolsa quedó registrado contra lo esperado. Contá la caja fuerte para actualizar su saldo.',
        }),
        problem: {
          title: 'No se pudo registrar el conteo de la bolsa',
          message: 'No se cambió nada. Revisá la conexión e intentá de nuevo.',
        },
        wentIntoBag: 'Entró en la bolsa',
        exactResult: 'Está todo. Quedó registrado con tu nombre y la hora.',
        differentResult:
          'La diferencia quedó registrada, no eliminada. La caja fuerte conserva el saldo indicado por la entrega hasta que la contés; ese conteo actualiza su saldo.',
        noteDescription:
          'Cualquier detalle que valga la pena recordar. Lo que escribás quedará guardado con el conteo.',
        record: 'Registrar contenido',
      },
      box: {
        title: (name) => `Conteo de ${name ?? 'caja'}`,
        notice: {
          title: 'Conteo registrado',
          message:
            'Este es el saldo de la caja a partir de ahora. Todo movimiento posterior se suma a esta cifra.',
        },
        problem: {
          title: 'No se pudo registrar el conteo de la caja',
          message: 'No se cambió nada. Revisá la conexión e intentá de nuevo.',
        },
        ledgerExpected: 'Saldo esperado',
        resultDescription:
          'La caja ahora muestra lo que contaste y la diferencia permanece en el registro en vez de quedar absorbida. Si contenía efectivo desde antes de usar este sistema, esa es la diferencia; ahora se puede volver a registrar una salida al banco.',
        contentsTitle: 'Contenido real',
        noteDescription: 'Cualquier detalle que valga la pena recordar sobre este conteo.',
        record: 'Registrar conteo',
      },
      standing: {
        title: 'Recibos más efectivo',
        description:
          'Esta caja se mantiene con un monto fijo y algunas compras se pagan directamente de ella. Por eso, lo que contaste más los recibos registrados desde la última revisión debe sumar ese monto. Es una comparación distinta de la diferencia anterior, que mide el conteo contra el registro.',
        cashCounted: 'Efectivo contado',
        receipts: 'Recibos desde la última revisión',
        together: 'Total',
        shouldHold: 'Monto fijo',
        short: 'faltante',
        over: 'sobrante',
        joiner: ' y ',
        exact:
          'Los recibos más el efectivo todavía suman el monto fijo de la caja. Cada billete que no está en ella está respaldado por un recibo registrado.',
        shortage: (differences) =>
          `A los recibos más el efectivo les faltan ${differences} para llegar al monto fijo. Este faltante no está respaldado por ningún recibo registrado: o no se registró un pago o falta el dinero.`,
        overage: (differences) =>
          `Los recibos más el efectivo suman ${differences} por encima del monto fijo; la caja contiene más de lo establecido.`,
        mixed: (differences) =>
          `La comparación por moneda es ${differences}. Un faltante no está respaldado por un recibo registrado; un sobrante indica que la caja contiene más de su monto fijo.`,
      },
    },
  },
};

export function registerPageCopy(locale: PortalLocale): RegisterPageCopy {
  return locale === 'es-HN' ? ES : EN;
}
